/**
 * Dashboard Unifié Auto-Détection
 * Simple et élégant - détecte automatiquement Owner/Renter
 */

import React, { useState } from 'react';
import {
  Container,
  Box,
  Alert,
  Button,
  CircularProgress,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

// Hooks
import { useAutoDashboard } from '../../hooks/useDashboardUnified';

// Dashboards spécialisés
import OwnerDashboardOptimized from './OwnerDashboardOptimized';
import RenterDashboardOptimized from './RenterDashboardOptimized';

// Constants
import { DASHBOARD_TYPES } from '../../constants/dashboardConstants';

const DashboardUnified = () => {
  // Charger le type de dashboard mémorisé depuis localStorage
  const [manualType, setManualType] = useState(() => {
    const savedType = localStorage.getItem('dashboardType');
    return savedType || null;
  });
  
  // Mémoriser le type de dashboard actif dans localStorage quand il change
  const handleSetManualType = (newType) => {
    setManualType(newType);
    if (newType) {
      localStorage.setItem('dashboardType', newType);
    } else {
      localStorage.removeItem('dashboardType');
    }
  };
  
  // Auto-détection du type de dashboard
  const { 
    dashboard, 
    detectedType, 
    loading, 
    error, 
    refresh 
  } = useAutoDashboard({
    autoRefresh: true,
    refreshInterval: 300000
  });

  // Type effectif (manuel ou auto-détecté) avec validation
  const effectiveType = manualType || detectedType || DASHBOARD_TYPES.RENTER;
  const validatedType = Object.values(DASHBOARD_TYPES).includes(effectiveType) 
    ? effectiveType 
    : DASHBOARD_TYPES.RENTER;

  // Handlers
  const handleTypeChange = (event, newType) => {
    handleSetManualType(newType);
  };

  // Chargement initial
  if (loading && !detectedType && !manualType) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="50vh"
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Chargement de votre dashboard...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Détection automatique en cours
          </Typography>
        </Box>
      </Container>
    );
  }

  // Gestion des erreurs
  if (error && !detectedType && !manualType) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Réessayer
            </Button>
          }
        >
          Impossible de charger votre dashboard : {error}
        </Alert>
      </Container>
    );
  }

  // Interface de sélection manuelle si nécessaire
  const renderTypeSelector = () => (
    <Box mb={3}>
      <Typography variant="h6" gutterBottom>
        Choisissez votre vue
      </Typography>
      <Tabs
        value={validatedType}
        onChange={handleTypeChange}
        centered
        sx={{ mb: 2 }}
      >
        <Tab 
          value={DASHBOARD_TYPES.OWNER}
          label="Propriétaire"
          icon={<BusinessIcon />}
          iconPosition="start"
        />
        <Tab 
          value={DASHBOARD_TYPES.RENTER}
          label="Locataire"
          icon={<PersonIcon />}
          iconPosition="start"
        />
      </Tabs>
      
      {detectedType && (
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          Type détecté automatiquement : {detectedType === DASHBOARD_TYPES.OWNER ? 'Propriétaire' : 'Locataire'}
        </Typography>
      )}
    </Box>
  );

  // Rendu conditionnel des dashboards
  const renderDashboard = () => {
    if (!effectiveType) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box textAlign="center" mb={4}>
            <DashboardIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Bienvenue sur votre dashboard
            </Typography>
          </Box>
          {renderTypeSelector()}
        </Container>
      );
    }

    switch (validatedType) {
      case DASHBOARD_TYPES.OWNER:
        return <OwnerDashboardOptimized />;
      case DASHBOARD_TYPES.RENTER:
        return <RenterDashboardOptimized />;
      default:
        return (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="warning">
              Type de dashboard non reconnu : {validatedType}
            </Alert>
          </Container>
        );
    }
  };

  return (
    <>
      {/* Sélecteur de type si détection ambiguë */}
      {validatedType && detectedType && (
        <Container maxWidth="xl">
          <Box py={2} borderBottom={1} borderColor="divider">
            {renderTypeSelector()}
          </Box>
        </Container>
      )}
      
      {/* Dashboard principal */}
      {renderDashboard()}
    </>
  );
};

export default DashboardUnified;
